// Car and world sound (RALLY_GOAL.md §12): the recorded engine (AudioWorklet), turbo whistle, dump
// valve, anti-lag bangs, straight-cut gear whine, wind, the forest ambience and the spectators (P1-6);
// and the effects of driving, each coordinated with what makes it (src/audio/sfx-model.ts): tyre
// layers per side of the car from that side's wheels, stones from the wheel that throws them,
// suspension knocks, thumps, bump-stop clunks and landings from the corner that takes them, collisions
// that sound like what was hit from where it was hit, scrapes, splashes. Interior and exterior use
// different filtering and stereo width; the co-driver ducks the engine.
import type { AudioMixer } from './mixer';
import type { Vehicle } from '../sim/vehicle';
import workletUrl from './engine-worklet.ts?worker&url';
import { CrowdCue } from './crowd';
import engineBank from './engine-bank.json';
import type { EngineBankData } from './engine-granular';
import { SfxModel, type HitInput, type HitKind, type SfxEvent, type SideMix } from './sfx-model';

const sfxUrls = import.meta.glob('../assets/audio/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
const sfxUrl = (name: string) => sfxUrls[`../assets/audio/${name}.mp3`];

/** Teeth on the gear pair in mesh per gear (straight-cut whine ∝ shaft speed × teeth). */
const WHINE_TEETH = [0, 31, 27, 24, 21, 19];

/** Tyre loops, one copy per side of the car (tools/audio/real-sfx.ts). */
const SIDE_LOOPS = ['surf-gravel-slow', 'surf-gravel-fast', 'surf-gravel-slide', 'surf-grass', 'surf-tarmac-roll', 'surf-tarmac-squeal', 'surf-mud', 'surf-water'] as const;
type SideLoop = (typeof SIDE_LOOPS)[number];
interface LoopLayer {
  src: AudioBufferSourceNode;
  gain: GainNode;
}
interface Side {
  pan: StereoPannerNode;
  bus: GainNode;
  /** The gravel loops go through this low-pass: darker while the tyres scrub under braking. */
  gravelTone: BiquadFilterNode;
  /** Where this side sits outside the car (−1 left … +1 right). */
  dir: number;
  loops: Partial<Record<SideLoop, LoopLayer>>;
  rumble: GainNode;
}
/** Each side is one of two wheel pairs: their sum would be ~4 dB over the old single (whole-car) layer. */
const SIDE_GAIN = 0.62;

let seed = 4711;
const rnd = () => {
  seed = (seed * 16807) % 2147483647;
  return seed / 2147483647;
};

interface Sfx {
  stones?: AudioBuffer;
  impact?: AudioBuffer;
  bottoming?: AudioBuffer;
  splash?: AudioBuffer;
  forest?: AudioBuffer;
  'crowd-cheer'?: AudioBuffer;
  'crowd-murmur'?: AudioBuffer;
}

/** Real and generated one-shots by what makes them (tools/audio/real-sfx.ts). */
const ONE_SHOTS = {
  stone: Array.from({ length: 10 }, (_, k) => `stone-${k + 1}`),
  crash: ['crash-1'],
  wood: ['wood-1', 'wood-2', 'wood-3'],
  thud: ['thud-1'],
  splash: ['splash-real'],
  splashSmall: ['splash-small-1'],
  thump: ['thump-1', 'thump-2', 'thump-3', 'thump-4'],
  knock: ['knock-1', 'knock-2', 'knock-3'],
  bumpstop: ['bumpstop-1'],
  land: ['land-1'],
  belly: ['belly-1', 'belly-2', 'belly-3'],
  tap: ['tap-1'],
  tapWood: ['tap-wood-1'],
  hitRock: ['hit-rock-1'],
  hitTree: ['hit-tree-1'],
  hitFence: ['hit-fence-1'],
  hitBale: ['hit-bale-1', 'hit-bale-2'],
  hitBarrier: ['hit-barrier-1'],
  hitMetal: ['hit-metal-1'],
  hitBank: ['hit-bank-1'],
} as const;
type ShotKind = keyof typeof ONE_SHOTS;

/** Scrape-loop brightness by what the car is sliding along (Hz, low-pass). */
const SCRAPE_TONE: Record<HitKind, number> = { wood: 2000, timber: 2400, straw: 1100, bank: 1400, ground: 1600, rock: 6000, concrete: 5000, brick: 4500, metal: 8000 };

export class CarAudio {
  private ctx: AudioContext | null = null;
  private engine: AudioWorkletNode | null = null;
  private readonly sfx: Sfx = {};
  private ready = false;
  private failed = false;
  // Nodes
  private exterior!: GainNode;
  private interior!: GainNode;
  private turboOsc!: OscillatorNode;
  private turboGain!: GainNode;
  private turboNoise!: GainNode;
  private whineOsc!: OscillatorNode;
  private whineFilter!: BiquadFilterNode;
  private whineGain!: GainNode;
  private gravel!: GainNode;
  private grit!: GainNode;
  private tarmac!: GainNode;
  private squeal!: GainNode;
  private squealTone!: GainNode;
  private squealOsc!: OscillatorNode;
  private mud!: GainNode;
  private water!: GainNode;
  private wind!: GainNode;
  private windHiss!: GainNode;
  private windLoop: LoopLayer | null = null;
  private ambience!: GainNode;
  private oneShots!: GainNode;
  private horn!: GainNode;
  private bangBuffers: AudioBuffer[] = [];
  /** Real anti-lag bangs (tools/audio/real-sfx.ts); the procedural ones until they load. */
  private alsBuffers: AudioBuffer[] = [];
  /** Dump-valve blow-off one-shot (tools/audio/real-sfx.ts); the procedural flutter until it loads. */
  private dumpReal: AudioBuffer | null = null;
  private readonly real = Object.fromEntries(Object.keys(ONE_SHOTS).map((k) => [k, [] as AudioBuffer[]])) as Record<ShotKind, AudioBuffer[]>;
  private dumpBuffer: AudioBuffer | null = null;
  private crowdBus!: GainNode;
  private crowdPan!: StereoPannerNode;
  private murmur!: GainNode;
  private cue: CrowdCue | null = null;
  /** Cheers started so far (tests). */
  cheers = 0;
  /** One-shots played so far by kind (tools and tests: what the effects did). */
  readonly played: Record<string, number> = {};
  /** Set to an array by a tool to log every effect (AudioContext time, kind, material, pan, level). */
  eventLog: { t: number; kind: string; mat: string; pan: number; level: number; wheel: number }[] | null = null;
  // State
  private lastPops = 0;
  private lastThrottle = 0;
  private interiorMix = 0;
  /** True once the worklet plays the recorded engine bank (false: procedural fallback). */
  granular = false;
  private readonly sides: Side[] = [];
  /** Effects from the physics (src/audio/sfx-model.ts). */
  private readonly model = new SfxModel();
  private scrapeLoop: LoopLayer | null = null;
  private scrapeTone!: BiquadFilterNode;
  private scrapePan!: StereoPannerNode;
  /** Real surface loops go through this bus: muffled (low-passed) in the cockpit. */
  private surfBus!: GainNode;
  private surfLp!: BiquadFilterNode;

  constructor(private readonly mixer: AudioMixer) {}

  /** Build the graph once the AudioContext exists (first user gesture). */
  async init(ctx: AudioContext): Promise<void> {
    if (this.ctx) return;
    this.ctx = ctx;
    try {
      await ctx.audioWorklet.addModule(workletUrl);
      this.engine = new AudioWorkletNode(ctx, 'engine-synth', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2] });
    } catch (e) {
      console.warn('[audio] engine worklet unavailable', e);
      this.failed = true;
    }
    this.build(ctx);
    void this.loadSfx(ctx);
    void this.loadEngineBank(ctx);
    this.ready = true;
  }

  /** Recorded engine (engine-granular.ts): decode the bank here, hand the PCM to the worklet. */
  private async loadEngineBank(ctx: AudioContext): Promise<void> {
    const url = sfxUrl('engine-bank');
    if (!this.engine || !url) return;
    try {
      const r = await fetch(url);
      const buf = await ctx.decodeAudioData(await r.arrayBuffer());
      const pcm = buf.getChannelData(0).slice();
      this.engine.port.onmessage = (e: MessageEvent<{ granular?: boolean }>) => {
        if (e.data.granular) this.granular = true;
      };
      this.engine.port.postMessage({ bank: { pcm, data: engineBank as EngineBankData } }, [pcm.buffer]);
    } catch (e) {
      console.warn('[audio] engine bank failed to load; procedural engine instead', e);
    }
  }

  private gain(v = 0, to?: AudioNode): GainNode {
    const g = this.ctx!.createGain();
    g.gain.value = v;
    if (to) g.connect(to);
    return g;
  }

  private filter(type: BiquadFilterType, f: number, q = 0.7): BiquadFilterNode {
    const b = this.ctx!.createBiquadFilter();
    b.type = type;
    b.frequency.value = f;
    b.Q.value = q;
    return b;
  }

  private build(ctx: AudioContext): void {
    const engineBus = this.mixer.buses.engine;
    const fx = this.mixer.buses.effects;
    const amb = this.mixer.buses.ambience;
    // Engine: exterior = exhaust-forward; interior = muffled exhaust, intake and mechanics up front.
    // The exterior EQ was fitted to real rally pass-bys (third-octave spectra, tools/audio/ltas.ts):
    // the exhaust's firing fundamentals around 240 Hz up, the engine-bay mechanical band around
    // 1.2 kHz down (DECISIONS.md, audio realism).
    this.exterior = this.gain(1, engineBus);
    this.interior = this.gain(0, engineBus);
    if (this.engine) {
      const split = ctx.createChannelSplitter(2);
      this.engine.connect(split);
      const bark = this.filter('peaking', 240, 0.9);
      bark.gain.value = 9;
      const mech = this.filter('peaking', 1200, 0.8);
      mech.gain.value = -9;
      split.connect(bark, 0);
      bark.connect(mech);
      mech.connect(this.gain(0.9, this.exterior));
      split.connect(this.gain(0.35, this.exterior), 1);
      const muffle = this.filter('lowpass', 900, 0.7);
      split.connect(muffle, 0);
      muffle.connect(this.gain(0.75, this.interior));
      const presence = this.filter('peaking', 2400, 1);
      presence.gain.value = 3;
      split.connect(presence, 1);
      presence.connect(this.gain(0.6, this.interior));
    }
    // Turbo whistle and whoosh.
    this.turboGain = this.gain(0, engineBus);
    this.turboOsc = ctx.createOscillator();
    this.turboOsc.type = 'sine';
    this.turboOsc.frequency.value = 2500;
    this.turboOsc.connect(this.turboGain);
    this.turboOsc.start();
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer(ctx, 2);
    noise.loop = true;
    noise.start();
    const turboBand = this.filter('bandpass', 3500, 1.4);
    noise.connect(turboBand);
    this.turboNoise = this.gain(0, engineBus);
    turboBand.connect(this.turboNoise);
    // Straight-cut gear whine.
    this.whineOsc = ctx.createOscillator();
    this.whineOsc.type = 'sawtooth';
    this.whineFilter = this.filter('bandpass', 1500, 5);
    this.whineGain = this.gain(0, engineBus);
    this.whineOsc.connect(this.whineFilter).connect(this.whineGain);
    this.whineOsc.start();
    // Procedural surface layers (shared noise through per-surface filters): the fallback until the
    // recorded loops load.
    const band = (type: BiquadFilterType, f: number, q: number, out: GainNode) => {
      const b = this.filter(type, f, q);
      noise.connect(b);
      b.connect(out);
      return b;
    };
    this.surfLp = this.filter('lowpass', 7000, 0.5);
    this.surfLp.connect(fx);
    this.surfBus = this.gain(1, this.surfLp);
    this.gravel = this.gain(0, fx);
    band('bandpass', 520, 0.6, this.gravel);
    this.grit = this.gain(0, fx);
    band('highpass', 2800, 0.7, this.grit);
    this.tarmac = this.gain(0, fx);
    band('lowpass', 260, 0.7, this.tarmac);
    this.squeal = this.gain(0, fx);
    band('bandpass', 1150, 6, this.squeal);
    this.squealOsc = ctx.createOscillator();
    this.squealOsc.frequency.value = 1450;
    this.squealTone = this.gain(0, fx);
    this.squealOsc.connect(this.squealTone);
    this.squealOsc.start();
    this.mud = this.gain(0, fx);
    band('lowpass', 320, 1.2, this.mud);
    this.water = this.gain(0, fx);
    band('bandpass', 1900, 0.8, this.water);
    this.wind = this.gain(0, fx);
    band('lowpass', 550, 0.7, this.wind);
    this.windHiss = this.gain(0, fx);
    band('highpass', 4200, 0.7, this.windHiss);
    // The two sides of the car: their tyre loops and the road rumble, each through its own panner.
    for (const dir of [-1, 1]) {
      const pan = ctx.createStereoPanner();
      pan.pan.value = dir * 0.55;
      pan.connect(this.surfBus);
      const bus = this.gain(SIDE_GAIN, pan);
      const gravelTone = this.filter('lowpass', 6500, 0.5);
      gravelTone.connect(bus);
      // Rumble: the chassis working over rough ground, the noise's lowest octave.
      const rumble = this.gain(0, bus);
      band('lowpass', 120, 0.8, rumble);
      this.sides.push({ pan, bus, gravelTone, dir, loops: {}, rumble });
    }
    // One-shots share the surfaces' low-pass, so they are muffled in the cockpit as well.
    this.oneShots = this.gain(1, this.surfLp);
    // Scrape: a loop through a tone filter (what is being scraped) and a panner (which side).
    this.scrapePan = ctx.createStereoPanner();
    this.scrapePan.connect(this.oneShots);
    this.scrapeTone = this.filter('lowpass', 4000, 0.7);
    this.scrapeTone.connect(this.scrapePan);
    // Twin-tone horn: two detuned square waves through a resonant band (a car horn's ~400/500 Hz pair).
    this.horn = this.gain(0, fx);
    const hornBand = this.filter('bandpass', 900, 1.2);
    hornBand.connect(this.horn);
    for (const f of [415, 498]) {
      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.value = f;
      o.connect(hornBand);
      o.start();
    }
    this.ambience = this.gain(0.32, amb);
    // Spectators: murmur bed and cheers through one panner (the nearest hotspot).
    this.crowdPan = ctx.createStereoPanner();
    this.crowdPan.connect(amb);
    this.crowdBus = this.gain(1, this.crowdPan);
    this.murmur = this.gain(0, this.crowdBus);
    // Procedural one-shots: anti-lag bangs and dump-valve flutter.
    for (let k = 0; k < 4; k++) this.bangBuffers.push(this.bang(ctx, 55 + k * 12, 0.85 + 0.1 * k));
    this.dumpBuffer = this.dump(ctx);
  }

  private noiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const b = ctx.createBuffer(1, Math.round(ctx.sampleRate * seconds), ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = rnd() * 2 - 1;
    return b;
  }

  /** Anti-lag bang: a low thump (decaying sine with pitch drop) plus a noise crack. */
  private bang(ctx: AudioContext, f0: number, crack: number): AudioBuffer {
    const sr = ctx.sampleRate;
    const n = Math.round(sr * 0.32);
    const b = ctx.createBuffer(1, n, sr);
    const d = b.getChannelData(0);
    let ph = 0;
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const t = i / sr;
      const f = f0 * (1 + 1.5 * Math.exp(-t * 40));
      ph += (2 * Math.PI * f) / sr;
      const thump = Math.sin(ph) * Math.exp(-t * 14);
      const nz = rnd() * 2 - 1;
      lp += (nz - lp) * 0.35;
      const cr = lp * Math.exp(-t * 55) * crack;
      d[i] = Math.tanh((thump * 0.9 + cr) * 1.6) * 0.9;
    }
    return b;
  }

  /** Dump-valve flutter: band-limited noise chopped at ~24 Hz, decaying over 0.45 s. */
  private dump(ctx: AudioContext): AudioBuffer {
    const sr = ctx.sampleRate;
    const n = Math.round(sr * 0.45);
    const b = ctx.createBuffer(1, n, sr);
    const d = b.getChannelData(0);
    let lp = 0;
    let hp = 0;
    for (let i = 0; i < n; i++) {
      const t = i / sr;
      const nz = rnd() * 2 - 1;
      lp += (nz - lp) * 0.45;
      hp += (lp - hp) * 0.08;
      const am = 0.5 + 0.5 * Math.sin(2 * Math.PI * (24 - 8 * t) * t);
      d[i] = (lp - hp) * am * Math.exp(-t * 6) * 0.8;
    }
    return b;
  }

  private async decode(ctx: AudioContext, name: string): Promise<AudioBuffer | null> {
    const url = sfxUrl(name);
    if (!url) return null;
    try {
      const r = await fetch(url);
      return await ctx.decodeAudioData(await r.arrayBuffer());
    } catch (e) {
      console.warn(`[audio] ${name} failed to load`, e);
      return null;
    }
  }

  private startLoop(ctx: AudioContext, buf: AudioBuffer, to: AudioNode, offset: number): LoopLayer {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const gain = this.gain(0, to);
    src.connect(gain);
    src.start(0, offset % buf.duration);
    return { src, gain };
  }

  private async loadSfx(ctx: AudioContext): Promise<void> {
    for (const name of ['stones', 'impact', 'bottoming', 'splash', 'forest', 'crowd-cheer', 'crowd-murmur'] as const) {
      const b = await this.decode(ctx, name);
      if (b) this.sfx[name] = b;
    }
    // Tyre loops: one source per side, started half a loop apart so the two sides don't sound as one.
    for (const name of SIDE_LOOPS) {
      const buf = await this.decode(ctx, name);
      if (!buf) continue;
      const at = rnd() * buf.duration;
      const gravel = name === 'surf-gravel-slow' || name === 'surf-gravel-fast' || name === 'surf-gravel-slide';
      for (const side of this.sides) side.loops[name] = this.startLoop(ctx, buf, gravel ? side.gravelTone : side.bus, at + (side.dir > 0 ? buf.duration / 2 : 0));
    }
    const wind = await this.decode(ctx, 'surf-wind');
    if (wind) this.windLoop = this.startLoop(ctx, wind, this.surfBus, rnd() * wind.duration);
    const scrape = await this.decode(ctx, 'scrape-loop');
    if (scrape) this.scrapeLoop = this.startLoop(ctx, scrape, this.scrapeTone, 0);
    // One-shots: decoded in parallel (~50 short files), each kind keeping its variants' order.
    await Promise.all(
      (Object.keys(ONE_SHOTS) as ShotKind[]).map(async (kind) => {
        const bufs = await Promise.all(ONE_SHOTS[kind].map((name) => this.decode(ctx, name)));
        for (const b of bufs) if (b) this.real[kind].push(b);
      }),
    );
    this.dumpReal = await this.decode(ctx, 'dump-valve');
    for (let k = 1; k <= 6; k++) {
      const b = await this.decode(ctx, `als-${k}`);
      if (b) this.alsBuffers.push(b);
    }
    if (this.sfx.forest) {
      const src = ctx.createBufferSource();
      src.buffer = this.sfx.forest;
      src.loop = true;
      src.connect(this.ambience);
      src.start();
    }
    // The murmur clip is short: three copies at different rates and offsets so it never audibly loops.
    const mm = this.sfx['crowd-murmur'];
    if (mm) {
      for (const [rate, off] of [
        [0.87, 0],
        [1, 0.31],
        [1.13, 0.52],
      ]) {
        const src = ctx.createBufferSource();
        src.buffer = mm;
        src.loop = true;
        src.playbackRate.value = rate;
        src.connect(this.murmur);
        src.start(0, off % mm.duration);
      }
    }
  }

  /** Spectator hotspots for the crowd sound (P1-6). */
  setCrowds(sites: readonly { x: number; z: number }[]): void {
    this.cue = sites.length ? new CrowdCue(sites) : null;
  }

  /** Per frame: listener (camera) position and right vector on the ground; `interior` muffles it. */
  crowd(x: number, z: number, rightX: number, rightZ: number, interior: boolean): void {
    if (!this.ready || !this.ctx || !this.cue || this.ctx.state !== 'running') return;
    const c = this.cue;
    c.update(x, z, rightX, rightZ);
    const t = this.ctx.currentTime;
    const muffle = interior ? 0.5 : 1;
    this.murmur.gain.setTargetAtTime(0.5 * c.murmur * muffle, t, 0.4);
    this.crowdPan.pan.setTargetAtTime(c.pan * 0.7, t, 0.1);
    if (c.cheer && this.sfx['crowd-cheer']) {
      this.cheers++;
      // Two takes a beat apart at different pitches read as a bigger crowd.
      this.playCrowd(this.sfx['crowd-cheer'], 0.85 * c.cheerLevel * muffle, 0.95 + rnd() * 0.06, 0);
      this.playCrowd(this.sfx['crowd-cheer'], 0.6 * c.cheerLevel * muffle, 1.06 + rnd() * 0.06, 0.45);
    }
  }

  private playCrowd(buf: AudioBuffer, gain: number, rate: number, delay: number): void {
    const ctx = this.ctx!;
    const s = ctx.createBufferSource();
    s.buffer = buf;
    s.playbackRate.value = rate;
    const g = this.gain(gain, this.crowdBus);
    s.connect(g);
    s.onended = () => g.disconnect();
    s.start(ctx.currentTime + delay);
  }

  /** A one-shot from the centre (engine-borne sounds). */
  private play(buf: AudioBuffer | undefined | null, gain: number, rate = 1, offset = 0): void {
    if (!buf || !this.ctx) return;
    const s = this.ctx.createBufferSource();
    s.buffer = buf;
    s.playbackRate.value = rate;
    const g = this.gain(gain, this.oneShots);
    s.connect(g);
    s.onended = () => g.disconnect();
    s.start(0, offset);
  }

  /** A one-shot from a place on the car: `pan` −1 (left) … +1 (right), narrower in the cockpit. */
  private shot(buf: AudioBuffer | undefined | null, gain: number, rate: number, pan: number): void {
    if (!buf || !this.ctx || gain <= 0.002) return;
    const ctx = this.ctx;
    const s = ctx.createBufferSource();
    s.buffer = buf;
    s.playbackRate.value = rate;
    const g = this.gain(gain);
    const p = ctx.createStereoPanner();
    p.pan.value = pan * (1 - 0.45 * this.interiorMix);
    s.connect(g).connect(p).connect(this.oneShots);
    s.onended = () => {
      g.disconnect();
      p.disconnect();
    };
    s.start();
  }

  private pick(kind: ShotKind, at: number): AudioBuffer | null {
    const list = this.real[kind];
    return list.length ? list[Math.min(list.length - 1, Math.floor(at * list.length))] : null;
  }

  /**
   * Per frame. `interior`: cockpit/bonnet camera; `hit`: the frame's strongest chassis contact, what
   * it was and where (DriveSession.collision()), or null.
   */
  update(v: Vehicle, dt: number, interior: boolean, hit: HitInput | null): void {
    if (!this.ready || !this.ctx || this.ctx.state !== 'running') return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const tm = v.telemetry;
    const thr = v.applied.throttle;
    const rpm = tm.rpm;
    const maxT = v.spec.engine.torqueCurve.reduce((m, p) => Math.max(m, p[1]), 1);
    const load = Math.max(0, Math.min(1, v.drivetrain.engineTorque / maxT));
    this.engine?.port.postMessage({ rpm, throttle: thr, load: Math.max(load, thr * 0.3), boost: tm.boost });
    // Interior/exterior crossfade.
    this.interiorMix += ((interior ? 1 : 0) - this.interiorMix) * Math.min(1, dt * 8);
    const im = this.interiorMix;
    this.exterior.gain.setTargetAtTime(1 - im * 0.85, t, 0.03);
    this.interior.gain.setTargetAtTime(im, t, 0.03);
    // Turbo.
    const b = tm.boost;
    this.turboOsc.frequency.setTargetAtTime(1500 + 4000 * b, t, 0.05);
    this.turboGain.gain.setTargetAtTime(0.018 * b * b * (0.3 + 0.7 * thr) * (1 + im * 0.6), t, 0.05);
    this.turboNoise.gain.setTargetAtTime(0.05 * b * thr, t, 0.05);
    // Dump valve on a sharp lift with boost up.
    if (this.lastThrottle > 0.6 && thr < 0.2 && b > 0.5) this.play(this.dumpReal ?? this.dumpBuffer, (this.dumpReal ? 0.3 : 0.35) * b * (1 - im * 0.3), 0.92 + rnd() * 0.16);
    this.lastThrottle = thr;
    // Anti-lag bangs.
    const pops = v.drivetrain.popCount;
    const bangs = this.alsBuffers.length ? this.alsBuffers : this.bangBuffers;
    for (let k = Math.min(3, pops - this.lastPops); k > 0; k--) this.play(bangs[Math.floor(rnd() * bangs.length)], (0.45 + 0.45 * rnd()) * (1 - im * 0.4), 0.9 + rnd() * 0.2);
    this.lastPops = pops;
    // Gear whine (straight-cut box), louder in the car.
    const gear = Math.max(1, Math.min(5, tm.gear));
    const fw = (rpm / 60) * WHINE_TEETH[gear];
    this.whineOsc.frequency.setTargetAtTime(fw, t, 0.02);
    this.whineFilter.frequency.setTargetAtTime(fw, t, 0.02);
    const speed = tm.speed;
    const wh = tm.gear > 0 ? (0.01 + 0.025 * load) * Math.min(1, speed / 8) : 0;
    this.whineGain.gain.setTargetAtTime(wh * (0.6 + 1.6 * im), t, 0.05);
    // In the cockpit the tyres and stones come through the floor and arches: low-passed, quieter.
    this.surfLp.frequency.setTargetAtTime(7000 - 4000 * im, t, 0.05);
    this.surfBus.gain.setTargetAtTime(0.75 - 0.15 * im, t, 0.05);
    // Everything the wheels and the body did this frame (src/audio/sfx-model.ts).
    const m = this.model;
    m.step(v.wheels, (v.spec.chassis.mass * 9.81) / 4, speed, dt, hit, v.spec.tyre.radius);
    this.side(this.sides[0], m.left, t, im);
    this.side(this.sides[1], m.right, t, im);
    this.fallbacks(m.left, m.right, t, im, speed);
    for (let i = 0; i < m.count; i++) this.event(m.events[i], im);
    // Scrape along whatever the car is held against.
    if (this.scrapeLoop) {
      this.scrapeLoop.gain.gain.setTargetAtTime(0.55 * m.scrape * (1 - im * 0.2), t, 0.04);
      this.scrapeLoop.src.playbackRate.setTargetAtTime(0.8 + 0.4 * Math.min(1, speed / 15), t, 0.1);
      this.scrapeTone.frequency.setTargetAtTime(SCRAPE_TONE[m.scrapeKind], t, 0.05);
      this.scrapePan.pan.setTargetAtTime(m.scrapePan * (1 - 0.45 * im), t, 0.05);
    }
    // Wind and the forest.
    const ws = Math.min(1.4, speed / 45);
    if (this.windLoop) {
      this.windLoop.gain.gain.setTargetAtTime(0.7 * ws * ws * (1 - im * 0.6), t, 0.1);
      this.windLoop.src.playbackRate.setTargetAtTime(0.85 + 0.3 * Math.min(1, ws), t, 0.12);
    }
    this.wind.gain.setTargetAtTime(this.windLoop ? 0 : 0.22 * ws * ws * (1 - im * 0.7), t, 0.1);
    this.windHiss.gain.setTargetAtTime(this.windLoop ? 0 : 0.018 * ws * ws * (1 - im * 0.8), t, 0.1);
    this.ambience.gain.setTargetAtTime(0.32 * (1 - im * 0.6) * (1 - Math.min(0.6, speed / 60)), t, 0.3);
  }

  /** One side's tyre loops and rumble. */
  private side(s: Side, mix: SideMix, t: number, im: number): void {
    const sp = mix.speed;
    s.pan.pan.setTargetAtTime(s.dir * (0.55 - 0.3 * im), t, 0.05);
    // Braking on gravel: the tyres plough it, a low scrub (the slide loop, darker and lower) rather
    // than a bright hiss; the close crunch near a stop is darkened with it.
    s.gravelTone.frequency.setTargetAtTime(6500 - 4700 * mix.scrub, t, 0.06);
    this.loop(s, 'surf-gravel-slow', 0.28 * mix.gravelSlow, 0.8 + 0.4 * Math.min(1, sp / 8), t);
    this.loop(s, 'surf-gravel-fast', 0.5 * mix.gravelFast, 0.85 + 0.3 * Math.min(1, sp / 35), t);
    this.loop(s, 'surf-gravel-slide', Math.max(mix.slide, 0.55 * mix.scrub), 0.9 + 0.25 * Math.min(1, mix.slip) - 0.15 * mix.scrub, t, 0.04);
    this.loop(s, 'surf-grass', 0.65 * mix.grass, 0.85 + 0.3 * Math.min(1, sp / 20), t);
    this.loop(s, 'surf-tarmac-roll', 0.8 * mix.tarmac, 0.8 + 0.4 * Math.min(1, sp / 28), t);
    this.loop(s, 'surf-tarmac-squeal', mix.squeal, 0.94 + 0.12 * Math.min(1, mix.squeal), t, 0.04);
    this.loop(s, 'surf-mud', 0.9 * mix.mud, 0.85 + 0.3 * Math.min(1, sp / 20), t);
    this.loop(s, 'surf-water', mix.water, 0.9 + 0.2 * Math.min(1, sp / 20), t, 0.03);
    s.rumble.gain.setTargetAtTime(0.5 * mix.rumble * (1 + 0.5 * im), t, 0.05);
  }

  /** Procedural layers, centred, for any recorded loop that hasn't loaded (yet). */
  private fallbacks(l: SideMix, r: SideMix, t: number, im: number, speed: number): void {
    const has = (name: SideLoop) => !!this.sides[0].loops[name];
    const sp = Math.min(1, speed / 28);
    const outside = 1 - im * 0.45;
    const loose = (l.gravelFast + r.gravelFast + l.gravelSlow + r.gravelSlow) / 2;
    this.gravel.gain.setTargetAtTime(has('surf-gravel-fast') ? 0 : 0.42 * sp * Math.min(1, loose) * outside, t, 0.05);
    this.grit.gain.setTargetAtTime(has('surf-gravel-slide') ? 0 : 0.09 * (l.slide + r.slide) * 0.5 * outside, t, 0.05);
    this.tarmac.gain.setTargetAtTime(has('surf-tarmac-roll') ? 0 : 0.12 * (l.tarmac + r.tarmac) * 0.5, t, 0.05);
    const sq = (l.squeal + r.squeal) * 0.5;
    this.squeal.gain.setTargetAtTime(has('surf-tarmac-squeal') ? 0 : 0.25 * sq, t, 0.04);
    this.squealTone.gain.setTargetAtTime(has('surf-tarmac-squeal') ? 0 : 0.05 * sq, t, 0.04);
    this.squealOsc.frequency.setTargetAtTime(1300 + 400 * Math.min(1, sq), t, 0.05);
    this.mud.gain.setTargetAtTime(has('surf-mud') ? 0 : 0.35 * sp * (l.mud + r.mud) * 0.5, t, 0.05);
    this.water.gain.setTargetAtTime(has('surf-water') ? 0 : 0.4 * (l.water + r.water) * 0.5, t, 0.04);
  }

  /** Play one effect from the model with the sound of what made it. */
  private event(e: SfxEvent, im: number): void {
    this.played[e.kind] = (this.played[e.kind] ?? 0) + 1;
    this.eventLog?.push({ t: this.ctx!.currentTime, kind: e.kind, mat: e.mat, pan: e.pan, level: e.level, wheel: e.wheel });
    // Through the floor (stones, suspension) the cockpit hears more; splashes and hits a little less.
    const under = 0.7 + 0.5 * im;
    switch (e.kind) {
      case 'stone': {
        const hit = this.pick('stone', e.pick);
        if (hit) this.shot(hit, 0.45 * e.level * (0.55 + 0.7 * im), e.rate, e.pan);
        else this.shot(this.sfx.stones, 0.5 * e.level * (0.6 + 0.6 * im), e.rate, e.pan);
        break;
      }
      case 'knock':
        this.shot(this.pick('knock', e.pick) ?? this.sfx.bottoming, 0.75 * e.level * under, e.rate, e.pan);
        break;
      case 'thump':
        this.shot(this.pick('thump', e.pick) ?? this.sfx.bottoming, 0.95 * e.level * under, e.rate, e.pan);
        // A deep hole also bangs the chassis: the low thud under it.
        if (e.level > 0.7) this.shot(this.pick('thud', 0), 0.35 * e.level * under, 0.9 * e.rate, e.pan * 0.5);
        break;
      case 'bumpstop':
        this.shot(this.pick('bumpstop', 0) ?? this.sfx.bottoming, 0.85 * e.level * under, e.rate, e.pan);
        break;
      case 'land':
        this.shot(this.pick('land', 0) ?? this.sfx.bottoming, e.level * under, e.rate, 0);
        this.shot(this.pick('thud', 0), 0.55 * e.level * under, 0.9 * e.rate, 0);
        break;
      case 'belly':
        this.shot(this.pick('belly', e.pick) ?? this.sfx.bottoming, 0.7 * e.level * under, e.rate, 0);
        break;
      case 'splash':
        this.shot(this.pick('splashSmall', 0) ?? this.sfx.splash, 0.7 * e.level * (1 - 0.3 * im), e.rate, e.pan);
        break;
      case 'bigsplash':
        this.shot(this.pick('splash', 0) ?? this.sfx.splash, 0.95 * e.level * (1 - 0.3 * im), e.rate, 0);
        break;
      case 'tap':
        this.hitSound(e, 0.6 * e.level * (1 - 0.15 * im), true);
        break;
      case 'hit':
        this.hitSound(e, e.level * (1 - 0.15 * im), false);
        break;
    }
  }

  /** A collision: the material's own sound, with crumpling metal under the heavy ones. */
  private hitSound(e: SfxEvent, g: number, tap: boolean): void {
    const at = e.pick;
    switch (e.mat) {
      case 'wood':
        if (tap) this.shot(this.pick('tapWood', at) ?? this.pick('tap', at), g, e.rate, e.pan);
        else {
          this.shot(this.pick('hitTree', 0) ?? this.sfx.impact, g, e.rate, e.pan);
          this.shot(this.pick('wood', at), 0.55 * g, e.rate * 1.05, e.pan);
        }
        break;
      case 'timber':
        if (tap) this.shot(this.pick('wood', at) ?? this.pick('tap', at), 0.8 * g, e.rate * 1.1, e.pan);
        else this.shot(this.pick('hitFence', 0) ?? this.sfx.impact, g, e.rate, e.pan);
        break;
      case 'rock':
        this.shot(tap ? this.pick('tap', at) : (this.pick('hitRock', 0) ?? this.sfx.impact), g, e.rate, e.pan);
        break;
      case 'straw':
        this.shot(this.pick('hitBale', at) ?? this.pick('tap', at), tap ? 0.7 * g : g, e.rate, e.pan);
        break;
      case 'bank':
        this.shot(this.pick('hitBank', 0) ?? this.pick('thud', 0), tap ? 0.6 * g : g, e.rate, e.pan);
        if (!tap) this.shot(this.pick('thud', 0), 0.4 * g, 0.85 * e.rate, e.pan);
        break;
      case 'ground':
        this.shot(this.pick('belly', at), g, e.rate, 0);
        break;
      case 'metal':
        this.shot(tap ? this.pick('tap', at) : (this.pick('hitMetal', 0) ?? this.pick('hitBarrier', 0) ?? this.sfx.impact), g, e.rate, e.pan);
        break;
      default:
        // Concrete, brick.
        this.shot(tap ? this.pick('tap', at) : (this.pick('hitBarrier', 0) ?? this.sfx.impact), g, e.rate, e.pan);
    }
    if (e.heavy) this.shot(this.pick('crash', 0) ?? this.sfx.impact, 0.8 * g, 0.95 * e.rate, e.pan);
  }

  /** Current gain of each tyre loop, summed over both sides (tools and tests: which layers are sounding). */
  loopLevels(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const name of SIDE_LOOPS) {
      let g = 0;
      let loaded = false;
      for (const s of this.sides) {
        const l = s.loops[name];
        if (!l) continue;
        loaded = true;
        g += l.gain.gain.value;
      }
      if (loaded) out[name] = g;
    }
    if (this.windLoop) out['surf-wind'] = this.windLoop.gain.gain.value;
    return out;
  }

  /** Drive one side's copy of a tyre loop (no-op until it has loaded). */
  private loop(s: Side, name: SideLoop, level: number, rate: number, t: number, tc = 0.06): void {
    const l = s.loops[name];
    if (!l) return;
    l.gain.gain.setTargetAtTime(level, t, tc);
    l.src.playbackRate.setTargetAtTime(rate, t, 0.12);
  }

  setHorn(on: boolean): void {
    if (!this.ready || !this.ctx) return;
    this.horn.gain.setTargetAtTime(on ? 0.16 : 0, this.ctx.currentTime, on ? 0.01 : 0.03);
  }

  get available(): boolean {
    return this.ready && !this.failed;
  }
}
